import { useState } from 'react';
import { BOARD_LABEL } from './labels';
import type { CommentItem, PostItem } from '@suangua/shared';

export { BOARD_LABEL, boardLabel } from './labels';

/**
 * 通用头像：有图显示图（加载失败回退首字），无图显示用户名首字
 * size：xs=24 / sm=38 / lg=64 / xl=84（与原型 theme.css 同名类）
 */
export function Avatar({
  name,
  url,
  size = 'sm',
  ring = false,
  onClick,
}: {
  name: string;
  url?: string | null;
  size?: 'xs' | 'sm' | 'lg' | 'xl';
  ring?: boolean;
  onClick?: () => void;
}) {
  const [broken, setBroken] = useState(false);
  const showImg = !!url && !broken;
  const cls = `avatar-${size}${ring ? ' gold-ring' : ''}${onClick ? ' cursor-pointer' : ''}`;
  return (
    <div className={cls} onClick={onClick} title={onClick ? name : undefined}>
      {showImg ? (
        <img src={url as string} className="avatar-img" alt="" onError={() => setBroken(true)} />
      ) : (
        <span className="font-zh">{(name || '·').slice(0, 1)}</span>
      )}
    </div>
  );
}

/** 灵验印章文案（占卜贴 verdict） */
export function verdictLabel(v: string): string {
  return ({ accurate: '灵', inaccurate: '不灵', uncertain: '不好说' })[v] ?? '';
}

/** 灵验印章配色类 */
export function verdictClass(v: string): string {
  return ({ accurate: 'v-accurate', inaccurate: 'v-inaccurate', uncertain: 'v-uncertain' })[v] ?? '';
}

/** 吉凶徽章类（六神煞：吉/中吉/凶；其余评级回退到「中吉」中性样式） */
export function fortuneBadgeClass(f: string): string {
  return (
    { 吉: 'f-ji', 中吉: 'f-zhongji', 凶: 'f-xiong', 小凶: 'f-xiong' }[f] ?? 'f-zhongji'
  );
}

/** 吉凶圆点类（历史列表；含大六壬的 平 / 小凶） */
export function fortuneDotClass(f: string): string {
  return (
    ({
      吉: 'bg-jade-400',
      中吉: 'bg-gold-400',
      平: 'bg-t3',
      小凶: 'bg-cinnabar-500/70',
      凶: 'bg-cinnabar-500',
    })[f] ?? 'bg-fill'
  );
}

/** 帖子展示标题（占卜贴无标题时自动生成） */
export function postTitle(p: Pick<PostItem, 'id' | 'board' | 'title'>): string {
  return p.title || (p.board === 'divination' ? `占卜贴 #${p.id}` : '（无标题）');
}

/** 评论是否为楼主（与帖子作者同 user_id；公告帖作者为 null 恒不匹配） */
export function isOp(c: Pick<CommentItem, 'user_id'>, opUserId: number | null): boolean {
  return opUserId !== null && c.user_id === opUserId;
}

/** 相对时间展示（刚刚 / N 分钟前 / N 小时前 / 昨天 / 日期） */
export function fromNow(iso: string): string {
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return '';
  const diff = Date.now() - t;
  const min = Math.floor(diff / 60000);
  if (min < 1) return '刚刚';
  if (min < 60) return `${min} 分钟前`;
  const hour = Math.floor(min / 60);
  if (hour < 24) return `${hour} 小时前`;
  const day = Math.floor(hour / 24);
  if (day === 1) return '昨天';
  if (day < 7) return `${day} 天前`;
  const d = new Date(t);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}
